/* eslint-disable max-statements */
import { Injectable } from '@angular/core';
import { ProfilesService } from '@icc/construction-elements/configurator';
import { ConfigurationsService } from '@icc/configurator/shared';
import { ConfiguratorsDataService, EventBusService, BaseIssuesService } from '@icc/shared';
import { WindowActiveConfiguration } from '@icc/window-door/types';
import { DoorActiveConfiguration } from '@icc/window-door/types';
import type { DoorLightsSizeDto, DoorSizeDto } from '@icc/window-door/types';
import { ActiveSash } from '@icc/window-door/types';
import { IssueLevel, core } from '@icc/shared/helpers';
import { FillingsService } from '../../modules/glazings/fillings.service';
import { DoorSizes, Layout, Shape, expandShape, getDefaultShapeDimensions } from '@icc/window-door/types';
import { BehaviorSubject } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class SizesService {
    doorSizes: DoorSizeDto[] = [];
    doorLightsSizes: DoorLightsSizeDto[] = [];
    doorLightsSizes$ = new BehaviorSubject<DoorLightsSizeDto[]>([]);

    constructor(
        private configuratorsDataService: ConfiguratorsDataService,
        private profilesService: ProfilesService,
        private eventBusService: EventBusService,
        private fillingsService: FillingsService,
        private issuesService: BaseIssuesService,
        private configurationsService: ConfigurationsService,
    ) {
        this.eventBusService.subscribeWithoutConfiguration('initializedConfigurator', () => {
            this.updateMatchingDoorSizes();
        });
        this.eventBusService.subscribe(['setProfileSet', 'setFrameProfile'], data => {
            if (
                !DoorActiveConfiguration.is(data.activeConfiguration)
                || !data.activeConfiguration.System
                || !data.activeConfiguration.System.door_type
            ) {
                return;
            }
            this.updateMatchingDoorSizes(data.activeConfiguration as DoorActiveConfiguration);
        });
    }

    updateMatchingDoorSizes(conf = this.configurationsService.conf.Current) {
        if (DoorActiveConfiguration.is(conf) && this.configuratorsDataService?.data?.doorSizes && this.configuratorsDataService?.data?.doorLightsSizes) {
            this.doorSizes = this.configuratorsDataService.data.doorSizes.filter(
                size => conf.System && size.systems.includes(Number(conf.System.id))
            );
            this.doorLightsSizes = (this.configuratorsDataService.data.doorLightsSizes || []).filter(
                size => {
                    if (conf.Frames && conf.Frames[0]?.frame?.length > 0 && conf.Frames.length > 0 && conf.System) {
                        const frame = conf.Frames[0];
                        const profileId = frame.frame[1].profileId;
                        return (
                            size.systems.includes(Number(conf.System.id))
                            && size.profiles.includes(Number(profileId))
                        );
                    }
                }
            );
            this.doorLightsSizes$.next(this.doorLightsSizes);
        }
    }

    getSashesShorteningRange(conf = this.configurationsService.conf.Current as DoorActiveConfiguration): number[] {
        const range: number[] = [];
        const sashSizes = this.getSashSizes(conf) || [];
        const size = sashSizes.find(sash => sash.id === conf.doorSizes.sashSizeId);
        if(!size?.shortening_available) {
            return range;
        }
        (conf.System.shortening || []).forEach((s: { min: number; max: number; step: number }) => {
            let min = Number(s.min);
            const max = Number(s.max);
            const step = Number(s.step);
            if (max !== 0) {
                while (step > 0 ? max >= min : max <= min) {
                    if (!range.includes(min)) {
                        range.push(min);
                    }
                    min += step;
                }

                if (range.indexOf(0) === -1) {
                    range.unshift(0);
                }
            }
        });

        return range;
    }

    getSashWidthInRebate(sashWidth: number, conf: DoorActiveConfiguration) {
        const frameProfile =
        conf.UsedProfiles.find(p => p.type === 'frame')
            || this.profilesService.getProfile(conf.ProfileSet.frameSide);
        const passiveSash = conf.Sashes.find(s => s.type && ['DRP', 'DOP'].includes(s.type.type));
        const sashSizes = this.doorSizes.find(s => s.id === conf.doorSizes.sashSizeId);
        const usedSash = conf.UsedProfiles.find(p => p.type === 'virtual_door_sash');
        const sashProfileId = usedSash ? usedSash.id : conf.ProfileSet.sash;
        const size = sashSizes?.sizes[sashProfileId];
        if(passiveSash) {
           return sashWidth + (frameProfile?.spaceSash || 0) + ((size?.passive_sash_rabate || 0) / 2);
        }
        return sashWidth + (2 * frameProfile?.spaceSash || 0);
    }

    getSashHeightInRebate(sashHeight: number, conf: DoorActiveConfiguration) {
        const frame = conf.UsedProfiles.find(p => p.type === 'frame')
                || this.profilesService.getProfile(conf.ProfileSet.frameSide);
        const threshold = conf.UsedProfiles.find(p => p.type === 'threshold')
            || this.profilesService.getProfile(conf.ProfileSet.threshold);
        return (sashHeight + (frame?.spaceSash || 0) + (threshold?.spaceSash || 0))
    }

    isStandardWidthSelected(sashSizes: DoorSizeDto[], conf: DoorActiveConfiguration): boolean {
        if (sashSizes && conf.doorSizes?.sashSizeId) {
            return !sashSizes.find((s) => s.id === conf.doorSizes.sashSizeId)?.custom;
        }
    }

    isStandardHeightSelected(sashSize, conf: DoorActiveConfiguration): boolean {
        return !(
            !conf.doorSizes.isStandardHeightSelected ||
            (sashSize?.custom_height_availability &&
                !sashSize?.standard_default_height &&
                !sashSize?.default_height_availability)
        );
    }

    getConstructionWidthBasedOnLayout(layout: Layout, conf: DoorActiveConfiguration) {
        let width = 1000;
        if (layout && layout.sashes) {
            const sashSizes = this.doorSizes.find(s => s.id === conf.doorSizes.sashSizeId);
            const passiveSashSizes = this.doorSizes.find(
                s => s.id === conf.doorSizes.passiveSashSizeId
            );
            const usedSash = conf.UsedProfiles.find(p => p.type === 'virtual_door_sash');
            const sashProfileId = usedSash ? usedSash.id : conf.ProfileSet.sash;
            const frameProfile =
                conf.UsedProfiles.find(p => p.type === 'frame')
                || this.profilesService.getProfile(conf.ProfileSet.frameSide);
            const size = sashSizes?.sizes && sashSizes.sizes[sashProfileId];
            const passiveSize = passiveSashSizes && passiveSashSizes.sizes[sashProfileId];
            if (conf.doorSizes.isStandardWidthSelected) {
                if (size && layout.sashes.some(sash => ['DRA', 'DOA'].includes(sash.type))) {
                    width = size.sash_width + 2 * frameProfile?.spaceSash;
                }
            } else {
                width = conf.doorSizes.customWidth;
            }
            if (passiveSize && layout.sashes.some(sash => ['DRP', 'DOP'].includes(sash.type))) {
                width += passiveSize.sash_width + size.passive_sash_rabate;
            }
            layout.sashes.forEach(sash => {
                if (sash.type && sash.type === 'F') {
                    if (
                        sash.neighbors.left.length === 0
                        && sash.neighbors.right.length > 0
                        && sash.neighbors.bottom.length === 0
                    ) {
                        width += conf.doorSizes.leftLightSize;
                    } else if (
                        sash.neighbors.right.length === 0
                        && sash.neighbors.left.length > 0
                        && sash.neighbors.bottom.length === 0
                    ) {
                        width += conf.doorSizes.rightLightSize;
                    }
                }
            });
        }
        return width;
    }

    getSashSizes(conf: DoorActiveConfiguration) {
        const matchingSizes = this.getDoorSizesFromFillings(conf, 'doorActive');
        return (
            this.doorSizes.filter(
                size =>
                    !size.passive
                    && (matchingSizes.length === 0 || matchingSizes.includes(Number(size.id)))
            ) || []
        );
    }

    getNotPassiveSashesSizes() {
        return this.doorSizes.filter(size => !size.passive) || [];
    }

    getPassiveSashSizes(conf: DoorActiveConfiguration) {
        const matchingSizes = this.getDoorSizesFromFillings(conf, 'doorPassive');
        return (
            this.doorSizes.filter(
                size =>
                    size.passive
                    && (matchingSizes.length === 0 || matchingSizes.includes(Number(size.id)))
            ) || []
        );
    }

    getSideLightsSizes() {
        return this.doorLightsSizes.filter(size => size.type === 'side') || [];
    }

    getTopLightsSizes() {
        return this.doorLightsSizes.filter(size => size.type === 'top') || [];
    }

    getShapeFromDoorSizes(doorSizes: DoorSizes, conf: DoorActiveConfiguration): Shape {
        const { width, height } = this.getConstructionDimensions(doorSizes, conf);
        const shape = expandShape(
            getDefaultShapeDimensions('rect', width || conf.Width, height || conf.Height)
        );
        return shape;
    }

    getConstructionDimensions(doorSizes: DoorSizes, conf: DoorActiveConfiguration) {
        const sashSizes = this.doorSizes.find(s => s.id === doorSizes.sashSizeId);
        const passiveSashSizes = this.doorSizes.find(s => s.id === doorSizes.passiveSashSizeId);
        const usedSash = conf.UsedProfiles.find(p => p.type === 'virtual_door_sash');
        const sashProfileId = usedSash ? usedSash.id : conf.ProfileSet.sash;
        const frameProfile =
            conf.UsedProfiles.find(p => p.type === 'frame')
            || this.profilesService.getProfile(conf.ProfileSet.frameSide);
        const thresholdProfile =
            conf.UsedProfiles.find(p => p.type === 'threshold')
            || this.profilesService.getProfile(conf.ProfileSet.threshold);
        const size = sashSizes && sashSizes.sizes[sashProfileId];
        const passiveSize = passiveSashSizes && passiveSashSizes.sizes[sashProfileId];

        if (!size && !sashSizes?.custom) {
            this.issuesService.registerDataProblem(
                'no-matching-door-size',
                'Brak pasującego rozmiaru drzwi',
                conf,
                {
                    level: IssueLevel.FATAL,
                    extra: {
                        systemId: conf.System.id,
                        systemName: conf.System.name,
                        sashSize: sashSizes && sashSizes.id,
                        sashSizeName: sashSizes && sashSizes.name,
                        sashProfileId,
                    },
                }
            );
        } else {
            this.issuesService.unregister('no-matching-door-size', conf);
        }

        let height = 0;
        let width = 0;

        if ((sashSizes?.custom || sashSizes?.custom_height_availability) && (!conf.doorSizes.passiveSashSizeId)) {
            height = doorSizes.customHeight;
            width = doorSizes.customWidth;
        } else if (
            (sashSizes?.custom || sashSizes?.custom_height_availability) &&
            conf.doorSizes.passiveSashSizeId
        ) {
            height = doorSizes.customHeight;
            if (
                (conf.Sashes.some((sash) => sash.type && ['DRA', 'DOA'].includes(sash.type.type)) ||
                    conf.Sashes.length === 0) &&
                size
            ) {
                width += doorSizes.customWidth;
            }
            if (
                passiveSize &&
                conf.Sashes.some((sash) => sash.type && ['DRP', 'DOP'].includes(sash.type.type))
            ) {
                width += passiveSize.sash_width + (frameProfile?.spaceSash || 0) + ((size.passive_sash_rabate || 0) / 2);
            }
            conf.Sashes.forEach((sash) => {
                if (sash.type && sash.type.type === 'F') {
                    const frame = conf.Frames.find((f) => f.id === sash.frameId);
                    if (
                        sash.nearMullions.left === -1 &&
                        ((frame && frame.x) || 0) + sash.rx === 0
                    ) {
                        if (
                            sash.nearMullions.top === -1 &&
                            ((frame && frame.x) || 0) + sash.rx + sash.rWidth === conf.Width
                        ) {
                            height += doorSizes.topLightSize;
                        } else {
                            width += doorSizes.leftLightSize;
                        }
                    } else if (
                        sash.nearMullions.right === -1 &&
                        ((frame && frame.x) || 0) + sash.rx > 0
                    ) {
                        width += doorSizes.rightLightSize;
                    }
                }
            });
            if (doorSizes.shortening > 0) {
                height -= doorSizes.shortening;
            }
            return { width, height };
        } else {
            height = size ? size.sash_height + frameProfile?.spaceSash + thresholdProfile?.spaceSash : 0;
            if (
                (conf.Sashes.some(sash => sash.type && ['DRA', 'DOA'].includes(sash.type.type))
                    || conf.Sashes.length === 0)
                && size
            ) {
                width += size.sash_width + 2 * frameProfile?.spaceSash;
            }
            if (
                passiveSize
                && conf.Sashes.some(sash => sash.type && ['DRP', 'DOP'].includes(sash.type.type))
            ) {
                width += passiveSize.sash_width + size.passive_sash_rabate;
            }
        }
        conf.Sashes.forEach(sash => {
            if (sash.type && sash.type.type === 'F') {
                const frame = conf.Frames.find(f => f.id === sash.frameId);
                if (sash.nearMullions.left === -1 && ((frame && frame.x) || 0) + sash.rx === 0) {
                    if (
                        sash.nearMullions.top === -1
                        && ((frame && frame.x) || 0) + sash.rx + sash.rWidth === conf.Width
                    ) {
                        height += doorSizes.topLightSize;
                    } else {
                        width += doorSizes.leftLightSize;
                    }
                } else if (
                    sash.nearMullions.right === -1
                    && ((frame && frame.x) || 0) + sash.rx > 0
                ) {
                    width += doorSizes.rightLightSize;
                }
            }
        });
        if (doorSizes.shortening > 0) {
            height -= doorSizes.shortening;
        }
        return { width, height };
    }

    getSashWidth(
        sash: ActiveSash,
        conf: DoorActiveConfiguration,
        leftSashSchemaType = null,
        rightSashSchemaType = null
    ) {
        const sashSizes = this.doorSizes.find(s => s.id === conf.doorSizes.sashSizeId);
        const passiveSashSizes = this.doorSizes.find(
            s => s.id === conf.doorSizes.passiveSashSizeId
        );
        const usedSash = conf.UsedProfiles.find(p => p.type === 'virtual_door_sash');
        const sashProfileId = usedSash ? usedSash.id : conf.ProfileSet.sash;
        const frameProfile =
            conf.UsedProfiles.find(p => p.type === 'frame')
            || this.profilesService.getProfile(conf.ProfileSet.frameSide);
        const size = sashSizes?.sizes[sashProfileId];
        const passiveSize = passiveSashSizes && passiveSashSizes.sizes[sashProfileId];
        let width = sash.rWidth;
        if (['DRP', 'DOP'].includes(sash.type.type) && passiveSize) {
            width = passiveSize.sash_width;
        }
        if (['DRA', 'DOA'].includes(sash.type.type) && size) {
            width = size.sash_width;
        }
        if (['DRA', 'DOA'].includes(sash.type.type) && !conf.doorSizes.isStandardWidthSelected) {
            width = conf.doorSizes.customWidth;
        }
        const frame = conf.Frames.find(f => f.id === sash.frameId);
        if (
            sash.type.type === 'F'
            && ((frame && frame.x) || 0) + sash.rx === 0
            && ((frame && frame.x) || 0) + sash.rx + sash.rWidth !== conf.Width
        ) {
            width = conf.doorSizes.leftLightSize;
        }
        if (sash.type.type === 'F' && ((frame && frame.x) || 0) + sash.rx > 0) {
            width = conf.doorSizes.rightLightSize;
        }

        if (sash.type.type !== 'F' && (conf.doorSizes.isStandardWidthSelected || ['DRP', 'DOP'].includes(sash.type.type))) {
            const lDiv = conf.Mullions.find(m => m.id === sash.nearMullions.left);
            const leftSash = lDiv && lDiv.multiAlignLeft.length > 0 ? lDiv.multiAlignLeft[0] : null;
            const leftType = leftSashSchemaType
                ? leftSashSchemaType.type
                : leftSash && leftSash.type
                ? leftSash.type.type
                : null;
            width +=
                leftType && ['DRA', 'DOA', 'DRP', 'DOP'].includes(leftType)
                    ? size.passive_sash_rabate / 2
                    : frameProfile?.spaceSash;

            const rDiv = conf.Mullions.find(m => m.id === sash.nearMullions.right);
            const rightSash =
                rDiv && rDiv.multiAlignRight.length > 0 ? rDiv.multiAlignRight[0] : null;
            const rightType = rightSashSchemaType
                ? rightSashSchemaType.type
                : rightSash && rightSash.type
                ? rightSash.type.type
                : null;
            width +=
                rightType && ['DRA', 'DOA', 'DRP', 'DOP'].includes(rightType)
                    ? size.passive_sash_rabate / 2
                    : frameProfile?.spaceSash;
        }
        return width;
    }

    getSashHeight(sash: ActiveSash, conf: DoorActiveConfiguration) {
        const sashSizes = this.doorSizes.find(s => s.id === conf.doorSizes.sashSizeId);
        const passiveSashSizes = this.doorSizes.find(
            s => s.id === conf.doorSizes.passiveSashSizeId
        );
        const usedSash = conf.UsedProfiles.find(p => p.type === 'virtual_door_sash');
        const sashProfileId = usedSash ? usedSash.id : conf.ProfileSet.sash;
        const frameProfile =
            conf.UsedProfiles.find(p => p.type === 'frame')
            || this.profilesService.getProfile(conf.ProfileSet.frameSide);
        const thresholdProfile =
            conf.UsedProfiles.find(p => p.type === 'threshold')
            || this.profilesService.getProfile(conf.ProfileSet.threshold);
        const size = sashSizes?.sizes[sashProfileId];
        const passiveSize = passiveSashSizes && passiveSashSizes.sizes[sashProfileId];
        let height = sash.rHeight;
        const notStandardHeightSelected = !conf.doorSizes.isStandardHeightSelected ||
            (sashSizes?.custom_height_availability &&
                !sashSizes?.standard_default_height &&
                !sashSizes?.default_height_availability);
        if (['DRP', 'DOP'].includes(sash.type.type) && passiveSize) {
            if (sashSizes?.custom_height_availability && notStandardHeightSelected) {
                height = conf.doorSizes.customHeight;
            } else {
                height = passiveSize.sash_height;
            }
        }
        if (['DRA', 'DOA', 'F'].includes(sash.type.type) && size) {
            height = size.sash_height;
        }
        if (['DRA', 'DOA', 'F'].includes(sash.type.type) && notStandardHeightSelected) {
            height = conf.doorSizes.customHeight;
            if (conf.doorSizes.shortening > 0) {
                height -= conf.doorSizes.shortening;
            }
        }
        const frame = conf.Frames.find(f => f.id === sash.frameId);
        if (
            sash.type.type === 'F'
            && ((frame && frame.x) || 0) + sash.rx === 0
            && ((frame && frame.x) || 0) + sash.rx + sash.rWidth === conf.Width
        ) {
            height = conf.doorSizes.topLightSize;
        } else if (!notStandardHeightSelected) {
            height += frameProfile?.spaceSash + thresholdProfile?.spaceSash;

            if (conf.doorSizes.shortening > 0) {
                height -= conf.doorSizes.shortening;
            }
        }
        return height;
    }

    getDoorSizesFromFillings(
        conf: DoorActiveConfiguration,
        type: 'doorActive' | 'doorActiveInner' | 'doorPassive'
    ) {
        const matchingFillings = this.fillingsService.getMatchingFillingsForDoorSashes(conf, type);
        const doorSizeIds: number[] = matchingFillings.reduce((doorSizes, filling) => {
            if (filling.door_sizes_ids) {
                filling.door_sizes_ids.forEach(sizeId => {
                    if (!doorSizes.includes(Number(sizeId))) {
                        doorSizes.push(Number(sizeId));
                    }
                });
            }
            return doorSizes;
        }, []);
        return doorSizeIds;
    }

    getSizeRangeId(sizeObj: DoorSizes){
        return this.doorSizes.find(s => s.id === sizeObj.sashSizeId)?.size_range_id;
    }


    getSashSizeById(id){
        return this.configuratorsDataService.data.sizeRanges.find(
            (range) => Number(range.id) === Number(id)
        );
    }

    getRange(doorSizes: DoorSizes, field?: 'customHeight' | 'customWidth') {
        const sashSizes = this.doorSizes.find(s => s.id === doorSizes.sashSizeId);
        const sizeRange = this.configuratorsDataService.data.sizeRanges.find(range => Number(range.id) === Number(sashSizes.size_range_id));
        if (sizeRange) {
            const sizesXY = (sizeRange.sizes || []).map(size => ({
                x: Number(size.width),
                y: Number(size.height),
            }));
            return {
                ...core.getRangeFromPoly(sizesXY),
                step: field === 'customWidth' ? (sashSizes?.size_interval_custom_width || 1) : (sashSizes?.size_interval_height || 1)
            };
        }
        return {
            minX: 0,
            minY: 0,
            maxX: Number.MAX_VALUE,
            maxY: Number.MAX_VALUE,
            step: field === 'customWidth' ? (sashSizes?.size_interval_custom_width || 1) : (sashSizes?.size_interval_height || 1)
        };
    }

    getDoorLightSize(currencySize, defaultSize) {
        const newSize = currencySize !== 0 ? currencySize : defaultSize.size_from;
        return newSize >= defaultSize.size_from && newSize <= defaultSize.size_to ? newSize : defaultSize.size_from;
    }


    getBiggerRebateValueFromVirtualDoorSash(conf: DoorActiveConfiguration | WindowActiveConfiguration) {
        const virtualDoorSash = conf.UsedProfiles.find(p => p.type === 'virtual_door_sash');

        if (virtualDoorSash) {
            if (virtualDoorSash.rebateWidth && virtualDoorSash.rebateWidthOut) {
                return Math.max(virtualDoorSash.rebateWidth, virtualDoorSash.rebateWidthOut)
            } else if (virtualDoorSash.rebateWidth && !virtualDoorSash.rebateWidthOut) {
                return virtualDoorSash.rebateWidth;
            } else if (!virtualDoorSash.rebateWidth && virtualDoorSash.rebateWidthOut) {
                return virtualDoorSash.rebateWidthOut
            } else {
                return 0;
            }
        }
    }

    findMatchingDoorLightsSizesToDefaultProfile(
        conf: DoorActiveConfiguration,
        type: 'side' | 'top'
    ) {
        return (this.configuratorsDataService.data.doorLightsSizes || [])
            .filter(size => {
                if (conf.Frames && conf.Frames.length > 0 && conf.System) {
                    const frame = conf.Frames[0];
                    const defaultProfile = this.profilesService.getDefaultFrameProfile(
                        'side',
                        frame,
                        conf,
                        1,
                        size.id
                    );
                    return (
                        defaultProfile
                        && size.systems.includes(Number(conf.System.id))
                        && size.profiles.includes(Number(defaultProfile.id))
                    );
                }
            })
            .filter(size => size.type === type);
    }

    setDoorSizesBasedOnLayout(layout: Layout, conf: DoorActiveConfiguration) {
        if (layout && layout.sashes) {
            let sideLightsSizes = this.getSideLightsSizes();
            let topLightsSizes = this.getTopLightsSizes();
            if (topLightsSizes.length === 0) {
                topLightsSizes = this.findMatchingDoorLightsSizesToDefaultProfile(conf, 'top');
            }
            if (sideLightsSizes.length === 0) {
                sideLightsSizes = this.findMatchingDoorLightsSizesToDefaultProfile(conf, 'side');
            }
            let hasLeftLight = false;
            let hasRightLight = false;
            let hasTopLight = false;
            layout.sashes.forEach(sash => {
                if (
                    sash.neighbors.left.length === 0
                    && sash.neighbors.right.length > 0
                    && sash.neighbors.bottom.length === 0
                    && sash.type === 'F'
                ) {
                    const leftSize = sideLightsSizes.find(
                        s => s.id === conf.doorSizes.leftLightSizeId
                    );
                    if (!leftSize) {
                        this.issuesService.registerDataProblem(
                            'no-matching-door-light-size',
                            'Brak pasującego rozmiaru drzwi',
                            conf,
                            {
                                level: IssueLevel.FATAL,
                                extra: {
                                    systemId: conf.System.id,
                                    systemName: conf.System.name,
                                    leftLightSizeId: conf.doorSizes.leftLightSizeId,
                                },
                            }
                        );
                    } else {
                        this.issuesService.unregister('no-matching-door-light-size', conf);
                        conf.doorSizes.leftLightSize = this.getDoorLightSize(conf.doorSizes.leftLightSize, leftSize);
                        hasLeftLight = true;
                    }
                }

                if (
                    sash.neighbors.right.length === 0
                    && sash.neighbors.left.length > 0
                    && sash.neighbors.bottom.length === 0
                    && sash.type === 'F'
                ) {
                    const rightSize = sideLightsSizes.find(
                        s => s.id === conf.doorSizes.rightLightSizeId
                    );
                    if (!rightSize) {
                        this.issuesService.registerDataProblem(
                            'no-matching-door-light-size',
                            'Brak pasującego rozmiaru drzwi',
                            conf,
                            {
                                level: IssueLevel.FATAL,
                                extra: {
                                    systemId: conf.System.id,
                                    systemName: conf.System.name,
                                    rightLightSizeId: conf.doorSizes.rightLightSizeId,
                                },
                            }
                        );
                    } else {
                        this.issuesService.unregister('no-matching-door-light-size', conf);
                        conf.doorSizes.rightLightSize = this.getDoorLightSize(conf.doorSizes.rightLightSize, rightSize);
                        hasRightLight = true;
                    }
                }

                if (
                    sash.neighbors.top.length === 0
                    && sash.neighbors.bottom.length > 0
                    && sash.type === 'F'
                ) {
                    const topSize = topLightsSizes.find(
                        s => s.id === conf.doorSizes.topLightSizeId
                    );
                    if (!topSize) {
                        this.issuesService.registerDataProblem(
                            'no-matching-door-light-size',
                            'Brak pasującego rozmiaru drzwi',
                            conf,
                            {
                                level: IssueLevel.FATAL,
                                extra: {
                                    systemId: conf.System.id,
                                    systemName: conf.System.name,
                                    topLightSizeId: conf.doorSizes.topLightSizeId,
                                },
                            }
                        );
                    } else {
                        this.issuesService.unregister('no-matching-door-light-size', conf);
                        conf.doorSizes.topLightSize = this.getDoorLightSize(conf.doorSizes.topLightSize, topSize);
                        hasTopLight = true;
                    }
                }
            });

            if (!hasLeftLight) {
                conf.doorSizes.leftLightSize = 0;
            }

            if (!hasRightLight) {
                conf.doorSizes.rightLightSize = 0;
            }

            if (!hasTopLight) {
                conf.doorSizes.topLightSize = 0;
            }
        }
    }
}
